import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, ArrowRight, CheckCircle2, Award, ShieldCheck, Hammer } from 'lucide-react';
import Header, { SocialRail } from '../components/Header';
import Footer from '../components/Footer';
import JsonLd from '../components/JsonLd';
import MiniGallerySection from '../components/MiniGallerySection';
import { cities, poolImages, HERO_IMAGE, COUNTY, SITE_URL } from '../data/cities';

const Home = () => {
  useEffect(() => {
    document.title = `Pool Builder in ${COUNTY.displayName} | American Pools & Spas`;
    const metaEl = document.querySelector('meta[name="description"]');
    if (metaEl) {
      metaEl.setAttribute(
        'content',
        `Custom pool builder in ${COUNTY.displayName} — serving ${COUNTY.featureCities} & more. Luxury inground pools, renovations & outdoor living. Call 407-606-6375.`
      );
    }
  }, []);

  // JSON-LD LocalBusiness — memoised so prop identity is stable
  const localBusinessLd = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'PoolBuilder',
      '@id': `${SITE_URL}/#localbusiness`,
      name: 'American Pools & Spas',
      image: HERO_IMAGE.startsWith('http') ? HERO_IMAGE : `${SITE_URL}${HERO_IMAGE}`,
      url: SITE_URL,
      telephone: '+1-407-606-6375',
      priceRange: '$$$',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '7320 Narcoossee Road',
        addressLocality: 'Orlando',
        addressRegion: 'FL',
        postalCode: '32822',
        addressCountry: 'US',
      },
      areaServed: { '@type': 'AdministrativeArea', name: COUNTY.displayName },
      sameAs: [
        'https://www.facebook.com/AmericanPoolsSpas',
        'https://www.instagram.com/americanpoolsspas/',
        'https://www.youtube.com/channel/UC29-XIIPCYvfEz-ZJvhlcag',
        'https://twitter.com/AmPoolsSpas',
        'https://www.pinterest.com/Am_Pools_Spas/',
        'https://www.houzz.com/pro/americanpoolsspas/__public',
      ],
    }),
    []
  );

  return (
    <div className="bg-white min-h-screen" data-testid="home-page">
      <JsonLd data={localBusinessLd} />
      <Header />
      <SocialRail />

      {/* Hero (H1) */}
      <section
        className="relative h-[460px] md:h-[560px] w-full bg-cover bg-center flex items-center justify-center"
        style={{
          backgroundImage: `linear-gradient(rgba(11,30,77,0.58), rgba(11,30,77,0.58)), url(${HERO_IMAGE})`,
        }}
        aria-label={`Pool builder in ${COUNTY.name} hero section`}
      >
        <div className="text-center text-white px-6 max-w-4xl">
          <p className="text-xs md:text-sm tracking-[0.3em] uppercase text-[#bcd5f5] mb-3">
            Serving {COUNTY.displayName.replace(', FL', ', Florida')}
          </p>
          <h1 className="font-serif text-4xl md:text-6xl font-bold leading-tight mb-5">
            Pool Builder in {COUNTY.displayName}
          </h1>
          <p className="text-base md:text-lg text-white/90 max-w-2xl mx-auto mb-8">
            Custom pool construction, pool remodeling, and luxury pool design
            throughout {COUNTY.name} &mdash; from Orlando and Winter Park to
            Windermere and Lake Buena Vista.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:4076066375"
              aria-label="Call American Pools and Spas at 407-606-6375"
              data-testid="hero-call-btn"
              className="inline-flex items-center gap-2 bg-[#1e4fa3] hover:bg-[#183d80] text-white font-semibold px-6 py-3 rounded-sm transition-colors"
            >
              <Phone className="w-4 h-4" /> 407-606-6375
            </a>
            <a
              href="https://american-pools.com/request-a-quote/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-quote-btn"
              className="inline-flex items-center gap-2 bg-white text-[#0b1e4d] hover:bg-gray-100 font-semibold px-6 py-3 rounded-sm transition-colors"
            >
              Request a Free Estimate <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Trust badges strip */}
      <section aria-label="Trust signals" className="bg-white border-b border-gray-200 py-6">
        <div className="max-w-[1280px] mx-auto px-5 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <div className="flex flex-col items-center gap-1">
            <Award className="w-6 h-6 text-[#1e4fa3]" />
            <p className="text-[13px] font-bold text-[#0b1e4d]">#1 Pool Builder</p>
            <p className="text-[11px] text-gray-500">13 Years Running</p>
          </div>
          <div className="flex flex-col items-center gap-1">
            <ShieldCheck className="w-6 h-6 text-[#1e4fa3]" />
            <p className="text-[13px] font-bold text-[#0b1e4d]">BBB A+ Accredited</p>
            <p className="text-[11px] text-gray-500">Torch Award Winner</p>
          </div>
          <div className="flex flex-col items-center gap-1">
            <Hammer className="w-6 h-6 text-[#1e4fa3]" />
            <p className="text-[13px] font-bold text-[#0b1e4d]">20+ Years Building</p>
            <p className="text-[11px] text-gray-500">Florida Lifestyle</p>
          </div>
          <div className="flex flex-col items-center gap-1">
            <CheckCircle2 className="w-6 h-6 text-[#1e4fa3]" />
            <p className="text-[13px] font-bold text-[#0b1e4d]">Licensed & Insured</p>
            <p className="text-[11px] text-gray-500">CPC1460475</p>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="max-w-4xl mx-auto px-5 py-14">
        <h2 className="font-serif text-3xl md:text-4xl text-[#0b1e4d] font-bold mb-5 text-center">
          Named the #1 Pool Builder for 13 Years Running!
        </h2>
        <p className="text-[15px] md:text-base text-gray-700 leading-relaxed text-center">
          <span className="font-semibold text-[#1e4fa3]">American Pools &amp; Spas</span>{' '}
          specializes in pool construction, renovation, and repair services, as
          well as custom pool and spa design for clients throughout {COUNTY.name},
          Florida. We enhance outdoor living spaces by creating stunning inground
          swimming pools that serve as a focal point for your home or business.
          Whether it&rsquo;s a residential or commercial project, we are your
          trusted partner for all your pool and outdoor living needs.
        </p>
        <p className="mt-6 pt-6 border-t border-gray-200 text-[#1e4fa3] font-bold tracking-wide uppercase text-sm text-center">
          Pool Builder {COUNTY.name} FL | Renovation &amp; Repair Services
        </p>
      </section>

      {/* Cities section */}
      <section id="cities" className="bg-[#f3f6fb] py-16">
        <div className="max-w-[1280px] mx-auto px-5">
          <div className="text-center mb-10">
            <p className="text-[#1e4fa3] tracking-[0.3em] text-xs uppercase mb-2 font-semibold">
              Service Areas
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#0b1e4d] font-bold mb-3">
              Pool Builder Serving All of {COUNTY.name}
            </h2>
            <p className="text-gray-600 text-[15px] max-w-2xl mx-auto">
              We proudly build custom pools across every city in {COUNTY.name}.
              Select your city below to learn more about our local pool-building
              services.
            </p>
          </div>

          <nav
            aria-label={`${COUNTY.name} cities`}
            data-testid="cities-nav"
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3"
          >
            {cities.map((city) => (
              <Link
                key={city.slug}
                to={`/pool-builder/${city.slug}/`}
                data-testid={`city-link-${city.slug}`}
                className="group flex items-center gap-2 px-4 py-3 bg-white border border-gray-200 rounded-sm hover:border-[#1e4fa3] hover:shadow-md transition-all"
                title={`Pool builder in ${city.displayName}`}
              >
                <MapPin className="w-4 h-4 text-[#1e4fa3] flex-shrink-0" />
                <span className="text-[13px] font-semibold text-[#0b1e4d] group-hover:text-[#1e4fa3]">
                  {city.name}
                </span>
              </Link>
            ))}
          </nav>
        </div>
      </section>

      {/* Gallery banner */}
      <section
        className="relative h-[360px] bg-cover bg-center flex items-center justify-center"
        style={{ backgroundImage: `url(${poolImages[1]})` }}
        aria-label="Custom swimming pool gallery preview"
      >
        <div className="absolute inset-0 bg-[#0b1e4d]/30" />
        <div className="relative bg-white/90 backdrop-blur-sm px-10 py-8 text-center max-w-xl mx-4">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#0b1e4d] tracking-wide">
            OUR CUSTOM SWIMMING POOL GALLERY
          </h2>
          <p className="mt-2 text-[#0b1e4d] text-sm">
            Building the Florida lifestyle for over 20 years
          </p>
          <a
            href="https://american-pools.com/orlando-pool-galleries/swimming-pools/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mt-5 bg-[#1e4fa3] hover:bg-[#183d80] text-white font-semibold text-sm tracking-wider uppercase px-8 py-3 rounded-sm transition-colors"
          >
            View Gallery
          </a>
        </div>
      </section>

      {/* Why choose us */}
      <section className="max-w-[1280px] mx-auto px-5 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div className="rounded-sm overflow-hidden shadow-lg order-2 lg:order-1">
            <img
              src={poolImages[2]}
              alt={`Custom screen-enclosed kidney-shaped pool built by American Pools & Spas in ${COUNTY.name}, Florida`}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="order-1 lg:order-2">
            <p className="text-[#1e4fa3] tracking-[0.3em] text-xs uppercase mb-2 font-semibold">
              Why Choose Us
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#0b1e4d] font-bold mb-5">
              {COUNTY.name}&rsquo;s Trusted Pool Builder
            </h2>
            <p className="text-gray-700 leading-relaxed text-[15px] mb-6">
              With over 20 years of Florida pool-building expertise, we bring
              craftsmanship and care to every backyard we transform throughout
              {' '}{COUNTY.name}.
            </p>
            <ul className="space-y-3">
              {[
                '#1 Ranked Pool Builder \u2013 13 Years Running',
                'BBB A+ Accredited Business & Torch Award Winner',
                'Fully licensed (CPC1460475) & insured Florida builder',
                'Custom 3D pool design and guided planning process',
                'Dedicated project manager from permit to completion',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#1e4fa3] flex-shrink-0 mt-0.5" />
                  <span className="text-[14px] text-gray-800">{item}</span>
                </li>
              ))}
            </ul>
            <a
              href="tel:4076066375"
              className="inline-flex items-center gap-2 mt-7 bg-[#1e4fa3] hover:bg-[#183d80] text-white font-semibold px-6 py-3 rounded-sm transition-colors"
            >
              <Phone className="w-4 h-4" /> Call 407-606-6375
            </a>
          </div>
        </div>
      </section>

      <MiniGallerySection poolImages={poolImages} countyName={COUNTY.name} />

      <Footer />
    </div>
  );
};

export default Home;
